import React, { useMemo } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Table,
  TableRow,
  TableHead,
  TableBody,
  TableCell
} from '@mui/material';
import { DollarSign, Package } from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip
} from 'recharts';
import { HourlySalesReportResponse } from 'src/services/apiService/response-models/report';

interface HourlySalesSummaryProps {
  hourlySalesSummary: HourlySalesReportResponse[] | null | undefined;
}

const SalesSummary: React.FC<HourlySalesSummaryProps> = ({ hourlySalesSummary }) => {
  // Memoize calculations for performance
  const summary = useMemo(() => {
    if (!hourlySalesSummary || hourlySalesSummary.length === 0) {
      return {
        totalRevenue: 0,
        totalQuantity: 0,
        chartData: [] as any[],
      };
    }
    const totalRevenue = hourlySalesSummary.reduce((sum, h) => sum + (h.totalSales || 0), 0);
    const totalQuantity = hourlySalesSummary.reduce((sum, h) => sum + (h.orderCount || 0), 0);
    const chartData = hourlySalesSummary.map((h) => ({
      hour: h.hour || '',
      sales: h.totalSales || 0,
      quantity: h.orderCount || 0,
    }));
    return { totalRevenue, totalQuantity, chartData };
  }, [hourlySalesSummary]);

  return (
    <div style={{ padding: 24 }}>
      <h2>Hourly Sales Summary</h2>
      {/* Summary Cards */}
      <div
        className="grid gap-4 md:grid-cols-2 lg:grid-cols-4"
        style={{
          display: 'grid',
          gap: 16,
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          marginBottom: 32
        }}
      >
        <Card>
          <CardHeader
            title={<span className="text-sm font-medium">Total Revenue</span>}
            action={<DollarSign size={20} style={{ color: '#888' }} />}
            style={{ paddingBottom: 0 }}
          />
          <CardContent>
            <div
              className="text-2xl font-bold"
              style={{ fontSize: 28, fontWeight: 700 }}
            >
              ${summary.totalRevenue.toFixed(2)}
            </div>
            <p
              className="text-xs text-muted-foreground"
              style={{ fontSize: 12, color: '#888' }}
            >
              Total revenue across all hours
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader
            title={<span className="text-sm font-medium">Items Sold</span>}
            action={<Package size={20} style={{ color: '#888' }} />}
            style={{ paddingBottom: 0 }}
          />
          <CardContent>
            <div
              className="text-2xl font-bold"
              style={{ fontSize: 28, fontWeight: 700 }}
            >
              {summary.totalQuantity}
            </div>
            <p
              className="text-xs text-muted-foreground"
              style={{ fontSize: 12, color: '#888' }}
            >
              Across {hourlySalesSummary ? hourlySalesSummary.length : 0} hours
            </p>
          </CardContent>
        </Card>
      </div>
      {/* Bar Chart Section */}
      <div
        className="grid gap-6 md:grid-cols-1"
        style={{
          display: 'grid',
          gap: 24,
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          marginBottom: 32
        }}
      >
        <Card>
          <CardHeader title={<span>Sales by Hour</span>} />
          <CardContent>
            <div style={{ width: '100%', height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={summary.chartData}
                  margin={{ top: 16, right: 16, left: 0, bottom: 40 }}
                >
                  <XAxis
                    dataKey="hour"
                    angle={-45}
                    textAnchor="end"
                    interval={0}
                    height={80}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis />
                  <ChartTooltip
                    formatter={(value: number, name: string) => {
                      if (name === 'sales') {
                        return [`$${value.toFixed(2)}`, 'Sales'];
                      }
                      if (name === 'quantity') {
                        return [value, 'Quantity Sold'];
                      }
                      return [value, name];
                    }}
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc' }}
                    itemStyle={{ color: '#333', fontSize: 12 }}
                    labelStyle={{ color: '#333', fontSize: 14 }}
                  />
                  <Bar dataKey="sales" fill="#2A9D90" name="Sales" />
                  <Bar dataKey="quantity" fill="#E76E50" name="Quantity Sold" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
      {/* Hourly Summary Table */}
      <div
        className="grid gap-6 md:grid-cols-1"
        style={{
          display: 'grid',
          gap: 24,
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          marginBottom: 32
        }}
      >
        <Card>
          <CardHeader title={<span>Hourly Sales Table</span>} />
          <CardContent>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Hour</TableCell>
                  <TableCell align="right">Total Sales</TableCell>
                  <TableCell align="right">Items Sold</TableCell>
                  <TableCell align="right">% of Total Sales</TableCell>
                  <TableCell align="right">% of Total Quantity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary.chartData.map((row) => (
                  <TableRow key={row.hour}>
                    <TableCell>{row.hour}</TableCell>
                    <TableCell align="right">${row.sales.toFixed(2)}</TableCell>
                    <TableCell align="right">{row.quantity}</TableCell>
                    <TableCell align="right">
                      {summary.totalRevenue ? ((row.sales / summary.totalRevenue) * 100).toFixed(1) : '0.0'}%
                    </TableCell>
                    <TableCell align="right">
                      {summary.totalQuantity ? ((row.quantity / summary.totalQuantity) * 100).toFixed(1) : '0.0'}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SalesSummary;
